import { createContext, useContext, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);

// id is composed of `${name}__${variant}` to differentiate Hot/Ice variants of same drink.
const makeId = (name, variant) => `${name}__${variant || "default"}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const add = useCallback((item) => {
    setItems((prev) => {
      const id = makeId(item.name, item.variant);
      const found = prev.find((p) => p.id === id);
      if (found) {
        return prev.map((p) =>
          p.id === id ? { ...p, qty: p.qty + 1 } : p
        );
      }
      return [...prev, { ...item, id, qty: 1 }];
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const inc = useCallback((id) => {
    setItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, qty: p.qty + 1 } : p))
    );
  }, []);

  const dec = useCallback((id) => {
    setItems((prev) =>
      prev
        .map((p) => (p.id === id ? { ...p, qty: p.qty - 1 } : p))
        .filter((p) => p.qty > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { totalQty, totalPrice } = useMemo(() => {
    let q = 0;
    let p = 0;
    items.forEach((it) => {
      q += it.qty;
      p += it.qty * it.price;
    });
    return { totalQty: q, totalPrice: p };
  }, [items]);

  const value = {
    items,
    add,
    remove,
    inc,
    dec,
    clear,
    open,
    setOpen,
    totalQty,
    totalPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
